/**
 * RepsDualPage — Sales Manager dual-tab Performance view.
 *
 * Ported from v2 Claude Design `aura-exec.jsx` → `PageRepsDual`.
 *   Personal tab: my own quota, pipeline, trend
 *     GET /api/v1/role-kpis/sales/personal/performance?userId=me
 *   Team tab: rep leaderboard
 *     GET /api/v1/role-kpis/sales/team/leaderboard
 *
 * Tab state persists in URL query param `?tab=personal|team`.
 * UI-only · no backend changes · tenant-agnostic (D-09).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageFrame,
  Tabs,
  Kpi,
  DataTable,
  Progress,
  Spark,
  Avatar,
  LoadingBlock,
  ErrorBlock,
  EmptyBlock,
  useFetch,
  compactINR,
  type DataTableRow,
} from '@/components/executive/ExecAtoms';

interface PersonalPerf {
  quotaAttainmentPct?: number;
  rank?: number;
  pipeline?: number;
  pipelineWeighted?: number;
  wonThisQuarter?: number;
  wonYoYPct?: number;
  activityScore?: string;
  callsMade?: number;
  meetingsHeld?: number;
  siteVisits?: number;
  trend?: { calls?: number[]; meetings?: number[]; visits?: number[] };
}

interface LeaderboardRow {
  userId?: string;
  name: string;
  openDeals?: number;
  wonValue?: number;
  quotaPct?: number;
  activities?: number;
}

const TABS = ['Team', 'Personal'];

export function RepsDualPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab')?.toLowerCase();
  const tab = rawTab === 'personal' ? 'Personal' : 'Team';
  const setTab = useCallback(
    (t: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', t.toLowerCase());
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const personal = useFetch<PersonalPerf>(
    tab === 'Personal' ? '/role-kpis/sales/personal/performance?userId=me' : null,
  );
  const team = useFetch<LeaderboardRow[] | { items: LeaderboardRow[] }>(
    tab === 'Team' ? '/role-kpis/sales/team/leaderboard' : null,
  );

  const leaderboard = useMemo<LeaderboardRow[]>(() => {
    if (!team.data) return [];
    return Array.isArray(team.data)
      ? team.data
      : Array.isArray((team.data as { items?: LeaderboardRow[] }).items)
        ? (team.data as { items: LeaderboardRow[] }).items
        : [];
  }, [team.data]);

  const loading = tab === 'Personal' ? personal.loading : team.loading;
  const err = tab === 'Personal' ? personal.error : team.error;

  return (
    <PageFrame
      title="Performance"
      sub="Quota attainment · pipeline · team leaderboard"
    >
      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {loading && <LoadingBlock />}
      {err && err !== 'not-available' && (
        <ErrorBlock message="Could not load performance data." />
      )}
      {err === 'not-available' && (
        <EmptyBlock message="Performance endpoint not available yet." />
      )}

      {!loading && !err && tab === 'Personal' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 10,
          }}
        >
          <Kpi
            label="Quota attainment"
            value={
              typeof personal.data?.quotaAttainmentPct === 'number'
                ? `${personal.data.quotaAttainmentPct}%`
                : '—'
            }
            delta={
              personal.data?.rank
                ? `Rank #${personal.data.rank} on team`
                : undefined
            }
            color="var(--success)"
          />
          <Kpi
            label="Pipeline"
            value={
              typeof personal.data?.pipeline === 'number'
                ? compactINR(personal.data.pipeline)
                : '—'
            }
            delta={
              typeof personal.data?.pipelineWeighted === 'number'
                ? `${compactINR(personal.data.pipelineWeighted)} weighted`
                : undefined
            }
          />
          <Kpi
            label="Closed won (Q)"
            value={
              typeof personal.data?.wonThisQuarter === 'number'
                ? compactINR(personal.data.wonThisQuarter)
                : '—'
            }
            delta={
              typeof personal.data?.wonYoYPct === 'number'
                ? `${personal.data.wonYoYPct > 0 ? '+' : ''}${personal.data.wonYoYPct}% YoY`
                : undefined
            }
            color="var(--success)"
          />
          <Kpi
            label="Activity score"
            value={personal.data?.activityScore ?? '—'}
          />

          <div
            className="card"
            style={{ padding: 14, gridColumn: 'span 4' }}
          >
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                marginBottom: 12,
              }}
            >
              Quarterly trend
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 14,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-3)',
                    marginBottom: 6,
                  }}
                >
                  Calls made
                </div>
                <div
                  style={{
                    fontFamily: 'var(--ff-serif)',
                    fontSize: 24,
                    fontWeight: 500,
                    color: 'var(--ink-0)',
                  }}
                >
                  {personal.data?.callsMade ?? '—'}
                </div>
                {personal.data?.trend?.calls && (
                  <Spark
                    values={personal.data.trend.calls}
                    w={160}
                    h={32}
                    color="var(--ink-0)"
                  />
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-3)',
                    marginBottom: 6,
                  }}
                >
                  Meetings held
                </div>
                <div
                  style={{
                    fontFamily: 'var(--ff-serif)',
                    fontSize: 24,
                    fontWeight: 500,
                    color: 'var(--ink-0)',
                  }}
                >
                  {personal.data?.meetingsHeld ?? '—'}
                </div>
                {personal.data?.trend?.meetings && (
                  <Spark
                    values={personal.data.trend.meetings}
                    w={160}
                    h={32}
                    color="var(--saffron-600)"
                  />
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-3)',
                    marginBottom: 6,
                  }}
                >
                  Site visits
                </div>
                <div
                  style={{
                    fontFamily: 'var(--ff-serif)',
                    fontSize: 24,
                    fontWeight: 500,
                    color: 'var(--ink-0)',
                  }}
                >
                  {personal.data?.siteVisits ?? '—'}
                </div>
                {personal.data?.trend?.visits && (
                  <Spark
                    values={personal.data.trend.visits}
                    w={160}
                    h={32}
                    color="var(--success)"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !err && tab === 'Team' && (
        <>
          {leaderboard.length === 0 ? (
            <EmptyBlock message="No leaderboard data yet." />
          ) : (
            <DataTable
              columns={[
                { k: 'n', label: 'Rep', w: '220px' },
                {
                  k: 'd',
                  label: 'Open deals',
                  w: '110px',
                  align: 'right',
                  mono: true,
                  muted: true,
                },
                {
                  k: 'won',
                  label: 'Won',
                  w: '110px',
                  align: 'right',
                  mono: true,
                },
                { k: 'quota', label: 'Quota', w: '220px' },
                {
                  k: 'calls',
                  label: 'Activities',
                  w: '120px',
                  align: 'right',
                  mono: true,
                  muted: true,
                },
              ]}
              rows={leaderboard.map((r): DataTableRow => ({
                n: () => (
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                    }}
                  >
                    <Avatar name={r.name} size={26} />
                    <span>{r.name}</span>
                  </div>
                ),
                d: r.openDeals ?? '—',
                won:
                  typeof r.wonValue === 'number'
                    ? compactINR(r.wonValue)
                    : '—',
                quota: () => (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Progress
                      value={r.quotaPct ?? 0}
                      color={
                        (r.quotaPct ?? 0) >= 80
                          ? 'var(--success)'
                          : (r.quotaPct ?? 0) >= 60
                            ? 'var(--saffron-600)'
                            : 'var(--danger)'
                      }
                      h={4}
                    />
                    <span className="num" style={{ fontSize: 11 }}>
                      {r.quotaPct ?? 0}%
                    </span>
                  </div>
                ),
                calls: r.activities ?? '—',
              }))}
            />
          )}
        </>
      )}
    </PageFrame>
  );
}

export default RepsDualPage;
